import axios from 'axios';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import fileDownload from 'js-file-download';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	getEudrInformationRequest,
	getEudrInformationSuccess,
	getEudrPlansRequest,
	getEudrPlansSuccess,
	getEudrStatusRequest,
	getEudrStatusSuccess,
	uploadFileRequest,
	uploadFileSuccess,
	downloadFileRequest,
	downloadFileSuccess,
	getUploadedFilesRequest,
	getUploadedFilesSuccess,
} from './reducer';
import {
	TEudrInformationResponsePayload,
	TEudrPlansResponsePayload,
	TEudrStatusResponsePayload,
	TEudrUploadedFilesRequestPayload,
	TEudrUploadedFilesResponsePayload,
} from './types';
import { getUploadedFiles } from './selectors';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getEudrStatusWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEudrStatusResponsePayload = yield call(api.eudr.getStatus);
		yield put(getEudrStatusSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(getEudrStatusSuccess(null));
	}
}

function* getEudrPlansWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEudrPlansResponsePayload = yield call(api.eudr.getPlans);
		yield put(getEudrPlansSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(getEudrPlansSuccess(null));
	}
}

function* getEudrInformationWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEudrInformationResponsePayload = yield call(api.eudr.getInformation);
		yield put(getEudrInformationSuccess(response));
	} catch (error) {
		// if (axios.isAxiosError(error))
		// notificationContainer(
		// 	error?.response?.data?.message ?? error.message ?? 'Request error',
		// 	'error',
		// );
		yield put(getEudrInformationSuccess(null));
	}
}

function* uploadFileWorker(action: PayloadAction<FormData>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.eudr.uploadFile, action.payload);
		yield put(uploadFileSuccess());
		notificationContainer('File uploaded successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(uploadFileSuccess());
	}
}

function* downloadFileWorker(action: PayloadAction<number>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: Blob = yield call(api.eudr.downloadFile, action.payload);
		const uploadedFiles: TEudrUploadedFilesResponsePayload = yield select(getUploadedFiles);

		yield put(downloadFileSuccess());
		if (response) {
			const file = uploadedFiles?.data?.find((item) => item.id === action.payload);
			yield fileDownload(response, file?.fileName ?? 'file');
		}
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(downloadFileSuccess());
	}
}

function* getUploadedFilesWorker(action: PayloadAction<TEudrUploadedFilesRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEudrUploadedFilesResponsePayload = yield call(
			api.eudr.getUploadedFiles,
			action.payload,
		);
		yield put(getUploadedFilesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(getUploadedFilesSuccess(null));
	}
}

export function* eudrSaga() {
	yield takeEvery(getEudrStatusRequest.type, getEudrStatusWorker);
	yield takeEvery(getEudrPlansRequest.type, getEudrPlansWorker);
	yield takeEvery(getEudrInformationRequest.type, getEudrInformationWorker);
	yield takeEvery(uploadFileRequest.type, uploadFileWorker);
	yield takeEvery(downloadFileRequest.type, downloadFileWorker);
	yield takeEvery(getUploadedFilesRequest.type, getUploadedFilesWorker);
}
